import React from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import {
  SceneShell,
  SlideChip,
  SlideTitle,
  Card3D,
  Punchline,
  DefBox,
  PlainBox,
  Pill,
  glow,
  spAppear,
} from "./shared";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/* ════════════ Slide 13 — The 12 skill kinds ════════════ */
const KINDS = [
  { name: "persona-rule", icon: "🧩", c: COLORS.neonBlue, line: "a guard line in an agent's prompt", gated: false },
  { name: "plan-template", icon: "📋", c: COLORS.neonCyan, line: "a reusable mission recipe", gated: false },
  { name: "workflow-recommendation", icon: "🧭", c: COLORS.neonGreen, line: "prefer workflow Y for intent X", gated: false },
  { name: "few-shot-exemplar", icon: "✅", c: COLORS.neonLime, line: "a good input/output example", gated: false },
  { name: "negative-exemplar", icon: "🚫", c: COLORS.neonOrange, line: "a past failure as anti-example", gated: false },
  { name: "glossary", icon: "📖", c: COLORS.neonYellow, line: "tenant-specific term definitions", gated: false },
  { name: "tool-preference", icon: "🔧", c: COLORS.neonAmber, line: "use tool Y over Z for a query class", gated: false },
  { name: "verify-rule", icon: "🛡️", c: COLORS.neonPink, line: "a tenant output quality gate", gated: false },
  { name: "memory-query", icon: "🧠", c: COLORS.neonPurple, line: "where to look to recall X", gated: false },
  { name: "cost-policy", icon: "💰", c: COLORS.neonCyan, line: "use a cheaper model for shape X", gated: false },
  { name: "recipient-change", icon: "📧", c: COLORS.neonRed, line: "change outbound recipients", gated: true },
  { name: "tool-allowlist-write", icon: "🔑", c: COLORS.neonRed, line: "grant a write / dangerous tool", gated: true },
];

export const S13Kinds: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonPurple;
  const ap = spAppear(frame, fps);
  const intro = spAppear(frame, fps, 6);
  const rot = frame * 0.55;
  const radius = 600;

  return (
    <SceneShell accent={accent}>
      <SlideChip n={13} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="One table — twelve levers"
        subtitle="A skill kind decides where in the pipeline a lesson plugs in"
        color={accent}
        appear={ap}
      />

      {/* 3D rotating carousel */}
      <div style={{ position: "absolute", top: 470, left: 960, width: 0, height: 0 }}>
        {KINDS.map((k, i) => {
          const ang = ((rot + i * 30) * Math.PI) / 180;
          const x = Math.sin(ang);
          const z = Math.cos(ang);
          const depth = (z + 1) / 2;
          const sc = (0.5 + depth * 0.62) * intro;
          const op = 0.2 + depth * 0.8;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: x * radius - 132,
                top: -160 + (1 - depth) * -16,
                width: 264,
                height: 286,
                opacity: op,
                transform: `scale(${sc})`,
                zIndex: Math.round(depth * 100),
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 20,
                  background: `linear-gradient(150deg, ${k.c}30, ${COLORS.bgCard}f4)`,
                  border: `2.4px solid ${k.c}`,
                  boxShadow: `0 0 34px ${k.c}${depth > 0.6 ? "88" : "33"}, 0 18px 36px rgba(0,0,0,0.55)`,
                  padding: 22,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 64 }}>{k.icon}</div>
                <div
                  style={{
                    fontSize: 19,
                    fontFamily: FONTS.mono,
                    fontWeight: 800,
                    color: k.c,
                    textShadow: glow(k.c),
                    textAlign: "center",
                  }}
                >
                  {k.name}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontFamily: FONTS.body,
                    color: COLORS.textSecondary,
                    textAlign: "center",
                    lineHeight: 1.35,
                  }}
                >
                  {k.line}
                </div>
                {k.gated && (
                  <div
                    style={{
                      fontSize: 13,
                      fontFamily: FONTS.mono,
                      fontWeight: 800,
                      color: COLORS.neonRed,
                      letterSpacing: 1,
                    }}
                  >
                    🔒 GATED — needs approval
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          position: "absolute",
          top: 706,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 40,
        }}
      >
        <DefBox
          term="Skill kind"
          body="The shape of a skill — it decides where the lesson plugs in: an agent's prompt, the planner, the verifier, the model selector, and more."
          color={accent}
          appear={spAppear(frame, fps, 50)}
          width={690}
        />
        <PlainBox
          body="The same notebook holds twelve types of page. Ten apply themselves; two with a lock wait for your OK."
          appear={spAppear(frame, fps, 66)}
          width={540}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 14 — Skill kinds in action #1 ════════════ */
export const S14Action1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonGreen;
  const ap = spAppear(frame, fps);

  const cards = [
    {
      kind: "persona-rule",
      icon: "🧩",
      c: COLORS.neonBlue,
      def: "Adds a guard line to an agent's role prompt.",
      ex: "\"Exclude medical 'Glean' results when researching.\"",
      effect: "Next run: no cross-entity contamination.",
    },
    {
      kind: "glossary",
      icon: "📖",
      c: COLORS.neonYellow,
      def: "Tenant-specific term definitions, injected everywhere.",
      ex: "\"In this tenant, EAKC = Enterprise AI Knowledge Companion.\"",
      effect: "Retrieval & answers grounded in your vocabulary.",
    },
    {
      kind: "verify-rule",
      icon: "🛡️",
      c: COLORS.neonPink,
      def: "A tenant output gate checked before delivery.",
      ex: "\"Reject any output containing 'TODO' or 'placeholder'.\"",
      effect: "Caught at verify — not in your inbox.",
    },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={14} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="Skill kinds in action — part 1"
        subtitle="Every kind ships with a plain definition and a worked example"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 312,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 42,
          perspective: 1700,
        }}
      >
        {cards.map((c, i) => (
          <Card3D key={i} inline w={448} h={448} color={c.c} frame={frame} delay={18 + i * 16} fps={fps} rotY={(i - 1) * 9}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ fontSize: 56 }}>{c.icon}</div>
              <div
                style={{
                  fontSize: 23,
                  fontFamily: FONTS.mono,
                  fontWeight: 800,
                  color: c.c,
                  textShadow: glow(c.c),
                }}
              >
                {c.kind}
              </div>
            </div>
            <div
              style={{
                fontSize: 18,
                fontFamily: FONTS.body,
                color: COLORS.textSecondary,
                marginTop: 14,
                lineHeight: 1.4,
              }}
            >
              {c.def}
            </div>
            <div
              style={{
                marginTop: 16,
                padding: "14px 16px",
                borderRadius: 12,
                background: `${c.c}14`,
                border: `1.6px solid ${c.c}66`,
              }}
            >
              <div style={{ fontSize: 13, fontFamily: FONTS.mono, color: c.c, fontWeight: 800, letterSpacing: 1.5 }}>
                EXAMPLE
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontFamily: FONTS.body,
                  color: COLORS.textPrimary,
                  marginTop: 6,
                  lineHeight: 1.4,
                  fontStyle: "italic",
                }}
              >
                {c.ex}
              </div>
            </div>
            <div
              style={{
                marginTop: 14,
                fontSize: 16,
                fontFamily: FONTS.body,
                color: COLORS.neonGreen,
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 900 }}>➜</span> {c.effect}
            </div>
          </Card3D>
        ))}
      </div>

      <Punchline
        text={
          <>
            A habit, a dictionary, an inspector — all{" "}
            <span style={{ color: accent }}>just pages</span>.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 78)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 15 — Skill kinds in action #2 ════════════ */
export const S15Action2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonAmber;
  const ap = spAppear(frame, fps);

  const cards = [
    {
      kind: "plan-template",
      icon: "📋",
      c: COLORS.neonCyan,
      ex: "Weekly competitive intel → web search → KB scan → smart_process → send.",
    },
    {
      kind: "workflow-recommendation",
      icon: "🧭",
      c: COLORS.neonGreen,
      ex: "Research briefs → use deep_research_brief, not raw web_search.",
    },
    {
      kind: "tool-preference",
      icon: "🔧",
      c: COLORS.neonAmber,
      ex: "Symbol / name lookups → try grep_kb before query_eakc.",
    },
    {
      kind: "cost-policy",
      icon: "💰",
      c: COLORS.neonPink,
      ex: "Daily summaries → Flash tier. A $2.00/run mission becomes $0.20/run.",
    },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={15} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="Skill kinds in action — part 2"
        subtitle="Recipes, routing hints, tool picks and money-savers"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 322,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 30,
          perspective: 1800,
        }}
      >
        {cards.map((c, i) => (
          <Card3D key={i} inline w={386} h={400} color={c.c} frame={frame} delay={16 + i * 13} fps={fps} rotY={(i - 1.5) * 8}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 70 }}>{c.icon}</div>
              <div
                style={{
                  fontSize: 19,
                  fontFamily: FONTS.mono,
                  fontWeight: 800,
                  color: c.c,
                  textShadow: glow(c.c),
                  textAlign: "center",
                }}
              >
                {c.kind}
              </div>
            </div>
            <div
              style={{
                marginTop: 18,
                padding: "16px 16px",
                borderRadius: 12,
                background: `${c.c}16`,
                border: `1.6px solid ${c.c}66`,
                flex: 1,
              }}
            >
              <div style={{ fontSize: 13, fontFamily: FONTS.mono, color: c.c, fontWeight: 800, letterSpacing: 1.5 }}>
                EXAMPLE
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontFamily: FONTS.body,
                  color: COLORS.textPrimary,
                  marginTop: 8,
                  lineHeight: 1.45,
                }}
              >
                {c.ex}
              </div>
            </div>
          </Card3D>
        ))}
      </div>

      <div style={{ position: "absolute", bottom: 56, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="The system doesn't only learn behaviour. It learns which recipe to cook, which tool to grab, and how to spend less — all as readable pages."
          appear={spAppear(frame, fps, 74)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 16 — The skill lifecycle ════════════ */
export const S16Lifecycle: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonCyan;
  const ap = spAppear(frame, fps);

  const states = [
    { name: "proposed", icon: "💡", c: COLORS.neonYellow, x: 250 },
    { name: "active", icon: "⚡", c: COLORS.neonGreen, x: 720 },
    { name: "stale", icon: "🕸️", c: COLORS.neonOrange, x: 1190 },
    { name: "archived", icon: "📦", c: COLORS.neonPurple, x: 1660 },
  ];

  // comet travelling straight along the track
  const trackY = 372;
  const xA = states[0].x;
  const xB = states[states.length - 1].x;
  const gp = (frame % 220) / 220;
  const cometFade = Math.max(0, Math.min(gp / 0.07, (1 - gp) / 0.07, 1));
  const appearComet = spAppear(frame, fps, 36);

  const invariants = [
    { icon: "🗄️", t: "Never deletes — only archives. Archive is recoverable." },
    { icon: "📌", t: "Pinned bypasses every auto-transition — your control valve." },
    { icon: "↩️", t: "Every apply is reversible — prior value is recorded." },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={16} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="The skill lifecycle"
        subtitle="Skills are managed — proposed, active, stale, archived"
        color={accent}
        appear={ap}
      />

      {/* connecting track */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {states.slice(0, 3).map((s, i) => (
          <line
            key={i}
            x1={s.x + 80}
            y1={trackY}
            x2={states[i + 1].x - 80}
            y2={trackY}
            stroke={accent}
            strokeWidth={4}
            strokeDasharray="12 9"
            strokeDashoffset={-(frame * 4) % 21}
            style={{ filter: `drop-shadow(0 0 7px ${accent})` }}
          />
        ))}
      </svg>

      {/* state nodes */}
      {states.map((s, i) => {
        const sp = spring({ frame: frame - 14 - i * 12, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], clamp);
        const sc = interpolate(sp, [0, 1], [0.4, 1], clamp);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: s.x - 92,
              top: 280 + Math.sin(frame * 0.03 + i) * 6,
              width: 184,
              height: 184,
              opacity: op,
              transform: `scale(${sc}) perspective(900px) rotateY(${Math.sin(frame * 0.012 + i) * 10}deg)`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 22,
                background: `linear-gradient(145deg, ${s.c}33, ${COLORS.bgCard}f2)`,
                border: `2.6px solid ${s.c}`,
                boxShadow: `0 0 34px ${s.c}66`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 56 }}>{s.icon}</div>
              <div
                style={{
                  fontSize: 22,
                  fontFamily: FONTS.mono,
                  fontWeight: 800,
                  color: s.c,
                  textShadow: glow(s.c),
                }}
              >
                {s.name}
              </div>
            </div>
          </div>
        );
      })}

      {/* travelling comet — rides the dashed track */}
      {[...Array(7)].map((_, k) => {
        const p = gp - k * 0.024;
        const inRange = p >= 0 && p <= 1;
        const x = xA + Math.max(0, Math.min(1, p)) * (xB - xA);
        const lead = k === 0;
        const sz = lead ? 26 : Math.max(6, 20 - k * 2.3);
        return (
          <div
            key={k}
            style={{
              position: "absolute",
              left: x - sz / 2,
              top: trackY - sz / 2,
              width: sz,
              height: sz,
              borderRadius: "50%",
              background: lead ? COLORS.textPrimary : accent,
              boxShadow: lead
                ? `0 0 20px ${COLORS.textPrimary}, 0 0 44px ${accent}`
                : `0 0 12px ${accent}`,
              opacity: inRange ? (lead ? 1 : Math.max(0, 0.55 - k * 0.07)) * cometFade * appearComet : 0,
            }}
          />
        );
      })}

      {/* side states */}
      <div
        style={{
          position: "absolute",
          top: 486,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 18,
          opacity: spAppear(frame, fps, 56),
        }}
      >
        <Pill text="↳ approved — gated kinds" color={COLORS.neonBlue} />
        <Pill text="↳ rejected — kept for audit" color={COLORS.neonRed} />
        <Pill text="📌 pinned — immortal" color={COLORS.neonYellow} />
      </div>

      {/* invariants */}
      <div
        style={{
          position: "absolute",
          top: 568,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 26,
        }}
      >
        {invariants.map((v, i) => (
          <div
            key={i}
            style={{
              width: 400,
              opacity: spAppear(frame, fps, 64 + i * 10),
              transform: `translateY(${(1 - spAppear(frame, fps, 64 + i * 10)) * 24}px)`,
              background: `linear-gradient(140deg, ${accent}18, ${COLORS.bgCard}f0)`,
              border: `2px solid ${accent}`,
              borderRadius: 16,
              padding: "18px 20px",
              display: "flex",
              gap: 14,
              alignItems: "center",
              boxShadow: `0 0 24px ${accent}33`,
            }}
          >
            <div style={{ fontSize: 40 }}>{v.icon}</div>
            <div style={{ fontSize: 17, fontFamily: FONTS.body, color: COLORS.textSecondary, lineHeight: 1.35 }}>
              {v.t}
            </div>
          </div>
        ))}
      </div>

      <div style={{ position: "absolute", bottom: 52, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <DefBox
          term="Curator"
          body="A background, idle-triggered worker that ages skills (active → stale after 30 days unused → archived after 90) — using a cheap model, never deleting anything."
          color={accent}
          appear={spAppear(frame, fps, 96)}
          width={1140}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 17 — Where skills come from ════════════ */
export const S17Sources: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonLime;
  const ap = spAppear(frame, fps);

  const sources = [
    { icon: "🔁", name: "improvement-analyzer", trig: "after every mission run", c: COLORS.neonBlue, y: 296 },
    { icon: "🏆", name: "novel-success distillation", trig: "a clever new mission that worked → a template", c: COLORS.neonGreen, y: 412 },
    { icon: "🛡️", name: "verify-output rejection", trig: "a repeated failure → a verify-rule", c: COLORS.neonPink, y: 528 },
    { icon: "🧠", name: "episode summarizer", trig: "a recurring pattern across sessions → a persona-rule", c: COLORS.neonPurple, y: 644 },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={17} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="Where skills come from"
        subtitle="Four sources write the notebook — so you don't have to"
        color={accent}
        appear={ap}
      />

      {/* converging arrows */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {sources.map((s, i) => (
          <path
            key={i}
            d={`M 730 ${s.y + 48} C 950 ${s.y + 48}, 1080 470, 1320 470`}
            fill="none"
            stroke={s.c}
            strokeWidth={3.5}
            strokeDasharray="10 9"
            strokeDashoffset={-(frame * 4) % 19}
            strokeOpacity={spAppear(frame, fps, 44 + i * 8) * 0.85}
            style={{ filter: `drop-shadow(0 0 6px ${s.c})` }}
          />
        ))}
      </svg>

      {/* source cards */}
      {sources.map((s, i) => {
        const sp = spring({ frame: frame - 14 - i * 11, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], clamp);
        const tx = interpolate(sp, [0, 1], [-160, 0], clamp);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: s.y,
              left: 150 + tx,
              width: 580,
              height: 96,
              opacity: op,
              transform: `perspective(1100px) rotateY(8deg)`,
              borderRadius: 14,
              background: `linear-gradient(135deg, ${s.c}2a, ${COLORS.bgCard}f0)`,
              border: `2.2px solid ${s.c}`,
              boxShadow: `0 0 26px ${s.c}55`,
              display: "flex",
              gap: 16,
              alignItems: "center",
              padding: "0 22px",
            }}
          >
            <div style={{ fontSize: 46 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 20, fontFamily: FONTS.mono, fontWeight: 800, color: s.c, textShadow: glow(s.c) }}>
                {s.name}
              </div>
              <div style={{ fontSize: 15, fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 4 }}>
                {s.trig}
              </div>
            </div>
          </div>
        );
      })}

      {/* central skills table */}
      <div
        style={{
          position: "absolute",
          left: 1320,
          top: 350,
          width: 320,
          height: 240,
          opacity: spAppear(frame, fps, 30),
          transform: `scale(${spAppear(frame, fps, 30)})`,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: -40,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${accent}44 0%, transparent 70%)`,
            transform: `scale(${1 + Math.sin(frame * 0.07) * 0.12})`,
          }}
        />
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderRadius: 22,
            background: `linear-gradient(145deg, ${accent}33, ${COLORS.bgCard}f4)`,
            border: `3px solid ${accent}`,
            boxShadow: `0 0 50px ${accent}88`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 84 }}>📕</div>
          <div
            style={{
              fontSize: 24,
              fontFamily: FONTS.heading,
              fontWeight: 900,
              color: accent,
              textShadow: glow(accent, true),
            }}
          >
            skills table
          </div>
          <div style={{ fontSize: 14, fontFamily: FONTS.mono, color: COLORS.textMuted }}>one source of truth</div>
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 54, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="The system writes its own notebook pages — after missions, after wins, after failures, and after spotting a habit it keeps repeating."
          appear={spAppear(frame, fps, 80)}
          width={1140}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 18 — The applier ════════════ */
export const S18Applier: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonGreen;
  const ap = spAppear(frame, fps);

  const beltPos = (frame % 120) / 120;

  const targets = [
    { icon: "📝", label: "agent persona", c: COLORS.neonBlue },
    { icon: "📋", label: "mission plan", c: COLORS.neonCyan },
    { icon: "🛡️", label: "the verifier", c: COLORS.neonPink },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={18} color={accent} appear={ap} label="The pivot" />
      <SlideTitle
        text="The applier — closing the loop"
        subtitle="Detection is half a loop; the applier makes the change real"
        color={accent}
        appear={ap}
      />

      {/* proposed skill */}
      <div style={{ position: "absolute", top: 340, left: 130, width: 300, opacity: spAppear(frame, fps, 14) }}>
        <Card3D w={300} h={170} color={COLORS.neonYellow} frame={frame} delay={14} fps={fps} inline rotY={9}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 52 }}>💡</div>
            <div style={{ fontSize: 19, fontFamily: FONTS.mono, fontWeight: 800, color: COLORS.neonYellow }}>
              proposed skill
            </div>
            <div style={{ fontSize: 14, color: COLORS.textMuted, textAlign: "center" }}>
              fresh from a source
            </div>
          </div>
        </Card3D>
      </div>

      {/* applier machine */}
      <div
        style={{
          position: "absolute",
          top: 320,
          left: 730,
          width: 320,
          height: 220,
          opacity: spAppear(frame, fps, 26),
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 22,
            background: `linear-gradient(145deg, ${accent}38, ${COLORS.bgCard}f4)`,
            border: `3px solid ${accent}`,
            boxShadow: `0 0 50px ${accent}77`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 70, transform: `rotate(${frame * 2}deg)` }}>⚙️</div>
          <div
            style={{
              fontSize: 26,
              fontFamily: FONTS.heading,
              fontWeight: 900,
              color: accent,
              textShadow: glow(accent, true),
            }}
          >
            APPLIER
          </div>
        </div>
      </div>

      {/* belt arrows */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        <line x1={440} y1={425} x2={720} y2={425} stroke={accent} strokeWidth={5} strokeDasharray="14 10" strokeDashoffset={-beltPos * 24} style={{ filter: `drop-shadow(0 0 8px ${accent})` }} />
        <line x1={1060} y1={425} x2={1340} y2={425} stroke={accent} strokeWidth={5} strokeDasharray="14 10" strokeDashoffset={-beltPos * 24} style={{ filter: `drop-shadow(0 0 8px ${accent})` }} />
      </svg>

      {/* targets */}
      <div style={{ position: "absolute", top: 312, right: 110, width: 460, display: "flex", flexDirection: "column", gap: 14 }}>
        {targets.map((t, i) => (
          <div
            key={i}
            style={{
              height: 76,
              opacity: spAppear(frame, fps, 40 + i * 10),
              transform: `perspective(1000px) rotateY(-9deg)`,
              borderRadius: 14,
              background: `linear-gradient(135deg, ${t.c}2a, ${COLORS.bgCard}f0)`,
              border: `2px solid ${t.c}`,
              boxShadow: `0 0 22px ${t.c}55`,
              display: "flex",
              gap: 14,
              alignItems: "center",
              padding: "0 22px",
            }}
          >
            <div style={{ fontSize: 40 }}>{t.icon}</div>
            <div style={{ fontSize: 21, fontFamily: FONTS.heading, fontWeight: 800, color: t.c, textShadow: glow(t.c) }}>
              patched: {t.label}
            </div>
          </div>
        ))}
      </div>

      {/* policy row */}
      <div
        style={{
          position: "absolute",
          top: 600,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 16,
          opacity: spAppear(frame, fps, 58),
        }}
      >
        <Pill text="✓ DEFAULT: auto-apply (10 low-risk kinds)" color={COLORS.neonGreen} />
        <Pill text="↩️ UNDO is the safety net" color={COLORS.neonCyan} />
        <Pill text="🔒 2 gated kinds need approval" color={COLORS.neonRed} />
      </div>

      {/* honest note */}
      <div
        style={{
          position: "absolute",
          top: 668,
          left: "50%",
          transform: "translateX(-50%)",
          width: 1100,
          opacity: spAppear(frame, fps, 72),
          background: `${COLORS.neonOrange}14`,
          border: `1.8px solid ${COLORS.neonOrange}aa`,
          borderRadius: 14,
          padding: "14px 24px",
          display: "flex",
          gap: 14,
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: 30 }}>⚠</div>
        <div style={{ fontSize: 17, fontFamily: FONTS.body, color: COLORS.textSecondary, lineHeight: 1.4 }}>
          <b style={{ color: COLORS.neonOrange }}>Honest status:</b> the applier has a real
          handler for <span style={{ fontFamily: FONTS.mono }}>persona-rule</span> today — the
          other 11 kinds are wired but still no-op. The loop is fully live for one kind, plumbed for the rest.
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <DefBox
          term="Applier"
          body="The worker that reads an approved skill and actually mutates the target — recording the prior value so a revert is one click."
          color={accent}
          appear={spAppear(frame, fps, 86)}
          width={1100}
        />
      </div>
    </SceneShell>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
